import React from 'react';
import { ScrollReveal } from '../ui/ScrollReveal';
import { EyeIcon, LockIcon } from 'lucide-react';
function PreviewPage({
  hooks,
  rotation,
  zIndex,
  delay
}: {
  hooks: {
    text: string;
    blurred: boolean;
  }[];
  rotation: string;
  zIndex: number;
  delay: number;
}) {
  return <ScrollReveal delay={delay} data-id="element-365">
      <div className="relative bg-white rounded-lg shadow-2xl shadow-black/40 p-5 md:p-7 w-full max-w-[320px] mx-auto" style={{
      transform: rotation,
      zIndex
    }} data-id="element-366">
        {/* Page header */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-neutral-200" data-id="element-367">
          <div className="flex items-center gap-2" data-id="element-368">
            <div className="w-5 h-5 rounded bg-accent/20 flex items-center justify-center" data-id="element-369">
              <span className="text-[8px] font-extrabold text-accent" data-id="element-370">HA</span>
            </div>
            <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider" data-id="element-371">
              Hook Arsenal
            </span>
          </div>
          <span className="text-[9px] text-neutral-300 font-mono" data-id="element-372">PDF</span>
        </div>

        {/* Hook entries */}
        <div className="space-y-3" data-id="element-373">
          {hooks.map((hook, i) => <div key={i} className="flex gap-2.5 items-start" data-id="element-374">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center mt-0.5" data-id="element-375">
                <span className="text-[9px] font-bold text-accent" data-id="element-376">
                  {i + 1}
                </span>
              </span>
              <div className="flex-1 min-w-0" data-id="element-377">
                {hook.blurred ? <div className="relative" data-id="element-378">
                    <p className="text-xs text-neutral-700 leading-relaxed blur-[3px] select-none" data-id="element-379">
                      {hook.text}
                    </p>
                    <div className="absolute inset-0 flex items-center justify-center" data-id="element-380">
                      <div className="flex items-center gap-1 bg-neutral-100 rounded-full px-2 py-0.5" data-id="element-381">
                        <LockIcon className="w-2.5 h-2.5 text-neutral-400" data-id="element-382" />
                        <span className="text-[8px] font-semibold text-neutral-400" data-id="element-383">
                          Desbloquear
                        </span>
                      </div>
                    </div>
                  </div> : <p className="text-xs text-neutral-700 leading-relaxed" data-id="element-384">
                    "{hook.text}"
                  </p>}
              </div>
            </div>)}
        </div>
      </div>
    </ScrollReveal>;
}
export function ProductPreviewSection() {
  return <section className="w-full py-20 md:py-32 px-4 md:px-5 overflow-hidden" data-id="element-385">
      <div className="max-w-3xl mx-auto" data-id="element-386">
        <ScrollReveal data-id="element-387">
          <div className="text-center mb-4" data-id="element-388">
            <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-full px-3.5 py-1.5 mb-5" data-id="element-389">
              <EyeIcon className="w-3.5 h-3.5 text-accent" data-id="element-390" />
              <span className="text-xs font-bold text-accent uppercase tracking-wider" data-id="element-391">
                Vista previa
              </span>
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-tight text-center mb-4" data-id="element-392">
            Mira lo que hay{' '}
            <span className="font-serif-italic text-accent-light" data-id="element-393">adentro</span>
          </h2>
          <p className="text-lg text-neutral-400 text-center mb-14 max-w-xl mx-auto" data-id="element-394">
            Ganchos listos para copiar, pegar y grabar. Sin pensar. Sin
            improvisar.
          </p>
        </ScrollReveal>

        {/* Stacked preview pages */}
        <div className="relative flex flex-col md:flex-row items-center justify-center gap-6 md:gap-0 md:-space-x-8" data-id="element-395">
          <PreviewPage delay={0.05} rotation="md:rotate-[-4deg]" zIndex={1} hooks={[{
          text: '¿Sabías que el 90% de los creadores pierden views por culpa de su primer segundo?',
          blurred: false
        }, {
          text: 'Lo que nadie te dice sobre el algoritmo de Reels...',
          blurred: false
        }, {
          text: 'Deja de hacer esto si quieres que tus videos se compartan',
          blurred: true
        }]} data-id="element-396" />

          <PreviewPage delay={0.15} rotation="md:rotate-[2deg] md:translate-y-4" zIndex={2} hooks={[{
          text: 'Esto es lo que hacen los creadores que crecen 10x más rápido que tú',
          blurred: false
        }, {
          text: 'El error que cometes en cada Reel sin darte cuenta',
          blurred: true
        }, {
          text: 'Si tu primer segundo se ve así, el algoritmo te está castigando',
          blurred: true
        }]} data-id="element-397" />

          <PreviewPage delay={0.25} rotation="md:rotate-[5deg] md:-translate-y-2" zIndex={3} hooks={[{
          text: 'Graba tu próximo Reel con este gancho y mira qué pasa',
          blurred: false
        }, {
          text: 'La frase que usan los creadores virales para abrir sus videos',
          blurred: true
        }, {
          text: 'Prueba esto antes de publicar tu siguiente video',
          blurred: true
        }]} data-id="element-398" />
        </div>

        <ScrollReveal delay={0.3} data-id="element-399">
          <p className="text-center text-neutral-500 text-sm mt-10" data-id="element-400">
            Estos son solo{' '}
            <span className="text-white font-semibold" data-id="element-401">9 de 200</span> ganchos
            incluidos en el arsenal.
          </p>
        </ScrollReveal>
      </div>
    </section>;
}